import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { LegalDisclaimer } from './LegalDisclaimer';

interface LegalUpdateView {
    id: string;
    document_id: string;
    title: string;
    summary_plain: string;
    impact_non_personal?: string | null;
    risk_level: string;
    jurisdiction: string | null;
    decision_date: string | null;
    topics: string[] | null;
    url: string;
}

interface NotificationTopic {
    id: string;
    key: string;
    label: string;
    description: string | null;
}

interface LegalUpdatesPageProps {
    onNavigate?: (page: string) => void;
}

const riskBadges: Record<string, string> = {
    info: 'bg-blue-100/70 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100',
    important: 'bg-amber-100/80 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100',
    critical: 'bg-rose-100/80 text-rose-900 dark:bg-rose-900/30 dark:text-rose-100',
};

const formatDisplayDate = (value?: string | null) => {
    if (!value) return 'Date pending';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'Date pending';
    return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const LegalUpdatesPage: React.FC<LegalUpdatesPageProps> = ({ onNavigate }) => {
    const [updates, setUpdates] = useState<LegalUpdateView[]>([]);
    const [topics, setTopics] = useState<NotificationTopic[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string>('all');
    const [jurisdiction, setJurisdiction] = useState<'all' | 'DE' | 'EU'>('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        const loadTopics = async () => {
            try {
                const { data, error: topicError } = await supabase
                    .from('notification_topics')
                    .select('id, key, label, description')
                    .eq('is_active', true)
                    .order('label');

                if (topicError) throw topicError;
                setTopics(data ?? []);
            } catch (err) {
                console.error('[LegalUpdatesPage] Failed to load topics', err);
            }
        };

        loadTopics();
    }, []);

    const fetchUpdates = async (withSpinner = true) => {
        if (withSpinner) setLoading(true);
        setError(null);

        try {
            let query = supabase
                .from('legal_updates_view')
                .select('*')
                .order('decision_date', { ascending: false, nullsFirst: false })
                .limit(100);

            if (selectedTopic !== 'all') {
                query = query.contains('topics', [selectedTopic]);
            }

            if (jurisdiction !== 'all') {
                query = query.eq('jurisdiction', jurisdiction);
            }

            const { data, error: updatesError } = await query;

            if (updatesError) {
                throw updatesError;
            }

            setUpdates(
                (data ?? []).map((item) => ({
                    id: item.id,
                    document_id: item.document_id,
                    title: item.title,
                    summary_plain: item.summary_plain,
                    impact_non_personal: item.impact_non_personal,
                    risk_level: item.risk_level ?? 'info',
                    jurisdiction: item.jurisdiction,
                    decision_date: item.decision_date,
                    topics: item.topics ?? [],
                    url: item.url,
                })),
            );
        } catch (err) {
            console.error('[LegalUpdatesPage] Failed to load updates', err);
            setError('Unable to load legal updates. Please try again later.');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchUpdates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTopic, jurisdiction]);

    const topicFilters = useMemo(() => {
        const base = [{ id: 'all', label: 'All topics' }];
        return base.concat(topics.map((topic) => ({ id: topic.key, label: topic.label })));
    }, [topics]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchUpdates(false);
    };

    return (
        <div className="flex-1 bg-luna-bg-light dark:bg-luna-bg-dark py-10 px-4 md:px-8">
            <div className="max-w-5xl mx-auto space-y-8">
                <div className="space-y-2 text-center md:text-left">
                    <p className="text-xs uppercase tracking-[0.2em] text-luna-text-light/60 dark:text-luna-text-dark/60">
                        Legal Radar
                    </p>
                    <h1 className="text-3xl md:text-4xl font-black text-luna-text-light dark:text-white">
                        EU & German Legal Updates
                    </h1>
                    <p className="text-sm md:text-base text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-3xl">
                        Curated legal developments from official public sources. Every summary is informational-only,
                        designed to help you identify which matters may require deeper review with your legal counsel.
                    </p>
                </div>

                <LegalDisclaimer variant="updates" />

                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-2">
                        {topicFilters.map((topic) => (
                            <button
                                key={topic.id}
                                type="button"
                                onClick={() => setSelectedTopic(topic.id)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                                    selectedTopic === topic.id
                                        ? 'bg-luna-secondary text-white border-luna-secondary'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light/80 dark:text-luna-text-dark/80 hover:border-luna-secondary/60'
                                }`}
                            >
                                {topic.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {(['all', 'DE', 'EU'] as const).map((value) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setJurisdiction(value)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                                    jurisdiction === value
                                        ? 'bg-luna-text-light text-white dark:bg-white/80 dark:text-luna-primary'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light/80 dark:text-luna-text-dark/80 hover:border-luna-secondary/60'
                                }`}
                            >
                                {value === 'all' ? 'All regions' : value}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={handleRefresh}
                            className="inline-flex items-center gap-1 rounded-full border border-luna-text-light/30 dark:border-luna-text-dark/30 px-3 py-1.5 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark hover:border-luna-secondary/60"
                        >
                            <span className={`material-symbols-outlined text-base ${isRefreshing ? 'animate-spin' : ''}`}>
                                refresh
                            </span>
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50/80 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-white/60 dark:bg-luna-primary/40 p-8 text-center text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                        Loading the latest legal updates...
                    </div>
                ) : updates.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-luna-text-light/30 dark:border-luna-text-dark/30 bg-white/50 dark:bg-luna-primary/20 p-8 text-center">
                        <p className="text-sm font-semibold text-luna-text-light dark:text-white mb-2">No updates yet</p>
                        <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                            We will notify you as soon as new statutes, decisions, or regulatory guidance land.
                        </p>
                        <button
                            type="button"
                            className="mt-4 text-xs font-semibold text-luna-secondary hover:underline"
                            onClick={() => onNavigate?.('Settings')}
                        >
                            Manage notification preferences →
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {updates.map((update) => (
                            <article
                                key={update.id}
                                className="rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-white dark:bg-luna-primary/70 p-5 shadow-sm shadow-black/5 dark:shadow-black/30"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-luna-text-light/60 dark:text-luna-text-dark/70">
                                    <span
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${riskBadges[update.risk_level] ?? riskBadges.info}`}
                                    >
                                        <span className="material-symbols-outlined text-sm">security</span>
                                        {update.risk_level === 'critical'
                                            ? 'Critical'
                                            : update.risk_level === 'important'
                                            ? 'Important'
                                            : 'Informational'}
                                    </span>
                                    <div className="flex items-center gap-3 text-[11px]">
                                        {update.jurisdiction && (
                                            <span className="uppercase tracking-wide">
                                                {update.jurisdiction === 'DE' ? 'Germany' : update.jurisdiction}
                                            </span>
                                        )}
                                        <span>{formatDisplayDate(update.decision_date)}</span>
                                    </div>
                                </div>
                                <h2 className="mt-3 text-lg font-semibold text-luna-text-light dark:text-white">
                                    {update.title}
                                </h2>
                                <p className="mt-3 text-sm text-luna-text-light/80 dark:text-luna-text-dark/80 whitespace-pre-wrap leading-relaxed">
                                    {update.summary_plain}
                                </p>
                                {update.impact_non_personal && (
                                    <div className="mt-4 rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50/70 dark:bg-blue-900/20 p-3 text-xs text-blue-900 dark:text-blue-100">
                                        <p className="font-semibold mb-1 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-sm">target</span>
                                            Who might this affect?
                                        </p>
                                        {update.impact_non_personal}
                                    </div>
                                )}
                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    {(update.topics ?? []).map((topic) => (
                                        <span
                                            key={`${update.id}-${topic}`}
                                            className="rounded-full bg-luna-text-light/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-luna-text-light/80 dark:bg-luna-text-dark/40 dark:text-luna-text-dark/80"
                                        >
                                            {topic}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
                                    <a
                                        href={update.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 rounded-full border border-luna-secondary px-3 py-1 font-semibold text-luna-secondary hover:bg-luna-secondary/10"
                                    >
                                        View official source
                                        <span className="material-symbols-outlined text-sm">open_in_new</span>
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => onNavigate?.('Settings')}
                                        className="inline-flex items-center gap-1 rounded-full text-luna-text-light/60 dark:text-luna-text-dark/70 hover:text-luna-secondary transition"
                                    >
                                        Subscribe to this topic
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                <div className="rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-white/60 dark:bg-luna-primary/60 p-6 text-center md:text-left">
                    <h3 className="text-lg font-semibold text-luna-text-light dark:text-white">Want proactive alerts?</h3>
                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                        Enable email or in-app notifications for the topics that matter most. You can update consent any
                        time inside your profile settings.
                    </p>
                    <button
                        type="button"
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-luna-secondary px-4 py-2 text-sm font-semibold text-white hover:bg-luna-secondary/90"
                        onClick={() => onNavigate?.('Settings')}
                    >
                        Manage notification settings
                        <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
